import { useState, useRef, useEffect } from 'react';
import { Map } from 'lucide-react';
import { PLACE_TYPE_ICONS } from './map-constants';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import type { AttributionLink } from './MapStyleControl';

interface MapLegendProps {
    activePlaceTypes: string[];
    activeEventCategories: string[];
    className?: string;
    showRelevanceLegend?: boolean;
    showAncientRoads?: boolean;
    showRomanProvinces?: boolean;
    showAncientEmpires?: boolean;
    attributions?: AttributionLink[];
}

export function MapLegend({
    activePlaceTypes,
    activeEventCategories,
    className = '',
    showRelevanceLegend = false,
    showAncientRoads = false,
    showRomanProvinces = false,
    showAncientEmpires = false,
    attributions = []
}: MapLegendProps) {
    const isDarkMode = useDarkMode();
    const containerRef = useRef<HTMLDivElement>(null);
    // Start collapsed by default
    const [isExpanded, setIsExpanded] = useState(false);

    // Close on click outside (Mobile only)
    useEffect(() => {
        if (!isExpanded) return;

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsExpanded(false);
            }
        };

        // Use click instead of mousedown to avoid closing while dragging the map
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isExpanded]);

    if (activePlaceTypes.length === 0 && activeEventCategories.length === 0) {
        return null;
    }

    // When collapsed, show nothing except the toggle pill
    // When expanded, show everything
    const displayedTypes = isExpanded ? activePlaceTypes : [];

    return (
        <div
            ref={containerRef}
            className={cn(
                "relative transition-all duration-300",
                className
            )}
        >
            {/* Toggle Pill */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={cn(
                    "flex items-center justify-center h-10 w-10 rounded-full backdrop-blur-md border shadow-lg transition-all",
                    isExpanded
                        ? isDarkMode
                            ? "bg-black/85 border-primary text-primary shadow-primary/20"
                            : "bg-white/95 border-primary text-primary shadow-primary/20"
                        : isDarkMode
                            ? "bg-black/75 border-white/20 text-white hover:bg-black/90"
                            : "bg-white/90 border-black/10 text-slate-900 hover:bg-white"
                )}
                title="Legend"
            >
                <Map className={cn("h-4 w-4", !isExpanded ? (isDarkMode ? "text-white/70" : "text-slate-900") : "text-primary")} />
            </button>

            {isExpanded && (
                <div className={cn(
                    "absolute top-0 right-12 w-64 backdrop-blur-md border shadow-xl rounded-lg animate-in fade-in slide-in-from-right-2 duration-200",
                    isDarkMode
                        ? "bg-black/85 border-white/30 text-white"
                        : "bg-white/95 border-black/15 text-slate-900"
                )}>
                    <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto">
                        {/* Place Types - Compact grid */}
                        {activePlaceTypes.length > 0 && (
                            <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                {displayedTypes.map((type) => {
                                    const icon = PLACE_TYPE_ICONS[type];
                                    if (!icon) return null;

                                    return (
                                        <div key={type} className="flex items-center gap-1 text-xs">
                                            <span className="text-sm">{icon}</span>
                                            <span className="capitalize text-xs truncate">{type.replace('-', ' ')}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Event Categories */}
                        {activeEventCategories.length > 0 && (
                            <div className={cn(
                                "flex items-center gap-1 text-xs pb-2 border-b",
                                isDarkMode ? "border-white/10" : "border-black/5"
                            )}>
                                <span>🕐</span>
                                <span>Events</span>
                            </div>
                        )}

                        {/* Ancient Roads */}
                        {showAncientRoads && (
                            <div className={cn(
                                "space-y-2 pt-2 border-t",
                                isDarkMode ? "border-white/10" : "border-black/5"
                            )}>
                                <h5 className={cn(
                                    "text-xs font-medium",
                                    isDarkMode ? "text-white/80" : "text-slate-600"
                                )}>Ancient Roads</h5>
                                <div className="grid grid-cols-1 gap-1.5 text-xs">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-[5px] rounded-sm flex-shrink-0" style={{ backgroundColor: '#8B4513' }} />
                                        <span>Main roads</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-[2.5px] rounded-sm flex-shrink-0" style={{ backgroundColor: '#A0826D' }} />
                                        <span>Secondary roads</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-[3px] rounded-sm flex-shrink-0" style={{ backgroundColor: '#4682B4' }} />
                                        <span>Rivers</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-[2px] rounded-sm flex-shrink-0" style={{ backgroundColor: '#5F9EA0' }} />
                                        <span>Sea lanes (toggle separately)</span>
                                    </div>
                                    <p className={cn(
                                        "text-[10px] italic mt-1",
                                        isDarkMode ? "text-white/60" : "text-slate-500"
                                    )}>
                                        💡 Tap/click roads for details
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Roman Provinces */}
                        {showRomanProvinces && (
                            <div className={cn(
                                "space-y-2 pt-2 border-t",
                                isDarkMode ? "border-white/10" : "border-black/5"
                            )}>
                                <h5 className={cn(
                                    "text-xs font-medium",
                                    isDarkMode ? "text-white/80" : "text-slate-600"
                                )}>Roman Provinces</h5>
                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#8B7355', opacity: 0.5 }} />
                                        <span className="text-[11px]">Judea</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#9B8B7E', opacity: 0.5 }} />
                                        <span className="text-[11px]">Syria</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#7B9B8B', opacity: 0.5 }} />
                                        <span className="text-[11px]">Asia</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#8B7B9B', opacity: 0.5 }} />
                                        <span className="text-[11px]">Macedonia</span>
                                    </div>
                                </div>
                                <p className={cn(
                                    "text-[10px] italic mt-1",
                                    isDarkMode ? "text-white/60" : "text-slate-500"
                                )}>
                                    💡 Click provinces for details
                                </p>
                            </div>
                        )}

                        {/* Ancient Empires */}
                        {showAncientEmpires && (
                            <div className={cn(
                                "space-y-2 pt-2 border-t",
                                isDarkMode ? "border-white/10" : "border-black/5"
                            )}>
                                <h5 className={cn(
                                    "text-xs font-medium",
                                    isDarkMode ? "text-white/80" : "text-slate-600"
                                )}>Ancient Empires</h5>
                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#6B4C9A', opacity: 0.4 }} />
                                        <span className="text-[11px]">Assyrian</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#4169A0', opacity: 0.4 }} />
                                        <span className="text-[11px]">Babylonian</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#3A9B9B', opacity: 0.4 }} />
                                        <span className="text-[11px]">Persian</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: '#9B5B4C', opacity: 0.4 }} />
                                        <span className="text-[11px]">Roman</span>
                                    </div>
                                </div>
                                <p className={cn(
                                    "text-[10px] italic mt-1",
                                    isDarkMode ? "text-white/60" : "text-slate-500"
                                )}>
                                    💡 Click empires for details
                                </p>
                            </div>
                        )}

                        {/* Place Relevance/Importance Indicators */}
                        {showRelevanceLegend && (
                            <div className={cn(
                                "space-y-2 pt-2 border-t",
                                isDarkMode ? "border-white/10" : "border-black/5"
                            )}>
                                <h5 className={cn(
                                    "text-xs font-medium",
                                    isDarkMode ? "text-white/80" : "text-slate-600"
                                )}>Place Relevance</h5>
                                <div className="grid grid-cols-1 gap-1 text-xs">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-[22px] h-[22px] rounded-full border-[4px] bg-white flex-shrink-0" style={{ borderColor: '#dc2626' }} />
                                        <span>High importance</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-[19px] h-[19px] rounded-full border-[3px] bg-white flex-shrink-0" style={{ borderColor: '#ef4444' }} />
                                        <span>Medium importance</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-[17px] h-[17px] rounded-full border-2 bg-white flex-shrink-0" style={{ borderColor: '#f87171' }} />
                                        <span>Low importance</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-[14px] h-[14px] rounded-full border-2 bg-white flex-shrink-0" style={{ borderColor: '#10b981' }} />
                                        <span>Event related</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-3 h-3 rounded-full border bg-white flex-shrink-0" style={{ borderColor: '#6b7280' }} />
                                        <span>General</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Map Attribution (Legal Compliance) */}
                    {attributions.length > 0 && (
                        <div className={cn(
                            "mt-4 p-4 border-t",
                            isDarkMode ? "border-white/10" : "border-black/5"
                        )}>
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                {attributions.map((attr, idx) => (
                                    <span key={idx} className="flex items-center text-[10px] leading-tight italic">
                                        <a
                                            href={attr.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn(
                                                "hover:underline transition-colors",
                                                isDarkMode ? "text-white/40 hover:text-white/60" : "text-slate-500 hover:text-slate-700"
                                            )}
                                        >
                                            {attr.label}
                                        </a>
                                        {idx < attributions.length - 1 && (
                                            <span className={cn("ml-2 select-none", isDarkMode ? "text-white/20" : "text-black/10")}>•</span>
                                        )}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

